import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, createBlock as _createBlock, withCtx as _withCtx, createSlots as _createSlots, Fragment as _Fragment } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "racq-max-w-xl5 w-full h-28 animate-pulse bg-racq-pale-blue-border mb-6"
}
const _hoisted_2 = { class: "rounded-md m-4 mb-0 px-4 py-3 bg-racq-info-blue" }
const _hoisted_3 = {
  key: 0,
  class: "text-sm"
}

import { computed, onMounted, ref } from 'vue';
import {
  InsurancePolicy,
  InsuranceQuoteType,
  QuoteTypeCodes,
  getInsuranceTypeDisplayName,
  RacqProductStatus,
  ProductContentFields,
} from '@/interfaces';
import ProductCard from '../ProductCard/ProductCard.vue';
import { useI18n } from 'vue-i18n';
import { formatDate } from 'date-fns';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import { replaceBookmark } from '@/utils/bookmark.replace';
import { ImageFieldValue } from '@sitecore-jss/sitecore-jss-vue/types/components/Image';
import ProductStatus from '../ProductStatus/ProductStatus.vue';
import ModalDialog from '../ModalDialog/ModalDialog.vue';
import SelectCoverageOption from '../SelectCoverageOption/SelectCoverageOption.vue';
import {
  getPolicyStatusDetails,
  IInsurancePolicyStatusRules,
  isAutoCancelledPolicyInGracePeriod,
} from '@/utils/self-service-rules/InsurancePolicyStatusRules';
import { LinkField } from '@sitecore-jss/sitecore-jss-vue/types/components/Link';
import { PolicyStatus } from '@/enums/policy-status.enum';
import { getInsuranceCoverageTypeToUseForIcon } from '@/utils/common-helper';
import { STANDARD_DATE_FORMAT } from '@/constants/formats';
import { useAccountStore } from '@/store';

// Define props and emitters
// ------------------------------------------------------------- //
export interface InsuranceProductCard {
  insuranceProduct: InsurancePolicy;
  fields?: ProductContentFields;
  dataTestId?: string;
}

enum Bookmarks {
  renewalStartDate = 'renewalStartDate',
  cancellationDate = 'cancellationDate',
  termEndDate = 'termEndDate',
  numberOfCoverages = 'numberOfCoverages',
  policyId = 'policyId',
  coverageId = 'coverageId',
  policyNumber = 'policyNumber',
}

export default /*@__PURE__*/_defineComponent({
  __name: 'InsuranceProductCard',
  props: {
    insuranceProduct: {},
    fields: {},
    dataTestId: {}
  },
  setup(__props: any) {

const props = __props;

// Define Store
// ------------------------------------------------------------- //
const translation = useI18n();
const dictionary: Record<string, string> = translation.getLocaleMessage('en') as any;
const policyStatusDetails = ref<IInsurancePolicyStatusRules>();
const accountStore = useAccountStore();
// Lifecycle hooks
// ------------------------------------------------------------- //

onMounted(async () => {
  try {
    isLoading.value = true;
    let renewalPolicy = undefined;

    if (props.insuranceProduct?.state?.isInRenewal == true) {
      renewalPolicy = await accountStore.fetchPolicyRenewal(props.insuranceProduct.policyNumber);
    }
    policyStatusDetails.value = await getPolicyStatusDetails(props.insuranceProduct, new Date(), true, renewalPolicy);
    showAdditionalWarning();
    isLoading.value = false;
  } catch (error) {
    console.error('Error loading insurance product', error);
  } finally {
    isLoading.value = false;
  }
});

// Define Component state
// ------------------------------------------------------------- //

const warningMessage = ref<string>('');
const isOverDueOrCancelled = ref<boolean>(false);
const showCoverageModal = ref<boolean>(false);
const selectCoverage = ref<string>('');
const isLoading = ref<boolean>(false);

// Define Computed state
// ------------------------------------------------------------- //

const title = computed(() => {
  if (policyStatusDetails.value?.isMultiCoveragePolicy) {
    return policyStatusDetails.value.areAllCoverageCodesSame
      ? getInsuranceTypeDisplayName(dictionary, props.insuranceProduct.coverages[0]?.coverageCode)
      : props.fields?.combinedPolicyTitle?.value;
  } else {
    return getInsuranceTypeDisplayName(dictionary, props.insuranceProduct.coverages[0]?.coverageCode);
  }
});

const subTitle = computed(() => {
  //if multi coverage policy
  if (policyStatusDetails.value?.isMultiCoveragePolicy) {
    if (props.insuranceProduct.coverages[0]?.isHome) {
      return replaceBookmark(
        Bookmarks.numberOfCoverages,
        props.fields?.multiPropertiesText?.value ?? '',
        props.insuranceProduct.coverages.length.toString()
      );
    } else if (props.insuranceProduct.coverages[0]?.isPet) {
      return replaceBookmark(
        Bookmarks.numberOfCoverages,
        props.fields?.multiPetText?.value ?? '',
        props.insuranceProduct.coverages.length.toString()
      );
    } else {
      return replaceBookmark(
        Bookmarks.numberOfCoverages,
        props.fields?.multiMotorText?.value ?? '',
        props.insuranceProduct.coverages.length.toString()
      );
    }
  } else {
    return policyStatusDetails.value?.coverageTitle;
  }
});

const isMultiCoveragePolicy = computed(() => props.insuranceProduct?.coverages?.length >= 2);
const renewalText = computed(() => {
  if (policyStatusDetails.value?.policyActiveNoPaymentDue) {
    return replaceBookmark(
      Bookmarks.termEndDate,
      props.fields?.renewalDueInfoText?.value ?? '',
      formatDate(new Date(props.insuranceProduct.termEnd ?? ''), STANDARD_DATE_FORMAT)
    );
  }
  return '';
});

const updatedCycloneText = computed(() => {
  if (policyStatusDetails.value?.showCycloneBanner) {
    let messageContent = props.insuranceProduct?.state?.isInRenewal
      ? props.fields?.updateCycloneDetailsTextRenewal?.value
      : props.fields?.updateCycloneDetailsText?.value;

    messageContent = replaceBookmark(
      Bookmarks.policyId,
      messageContent ?? '',
      props.insuranceProduct.policyNumber ?? ''
    );
    messageContent = replaceBookmark(
      Bookmarks.coverageId,
      messageContent ?? '',
      props.insuranceProduct.coverages[0].id
    );

    if (props.insuranceProduct?.renewalTermStart) {
      messageContent = replaceBookmark(
        Bookmarks.renewalStartDate,
        messageContent ?? '',
        formatDate(new Date(props.insuranceProduct.renewalTermStart), STANDARD_DATE_FORMAT)
      );
    }

    return messageContent;
  }
  return '';
});

const linkUrl = computed((): LinkField => {
  return {
    value: {
      href: replaceBookmark(
        Bookmarks.policyNumber,
        props.fields?.insurancePageUrl?.value?.href ?? '',
        props.insuranceProduct.policyNumber
      ),
      querystring: replaceBookmark(
        Bookmarks.policyNumber,
        props.fields?.insurancePageUrl?.value?.querystring ?? '',
        props.insuranceProduct.policyNumber
      ),
    },
  };
});

// Methods
// ------------------------------------------------------------- //

const fmtDate = (d?: Date | null) => (d ? formatDate(d, 'dd/MM/yyyy') : '');

const getIcon = () => {
  const useCoverageCode = getInsuranceCoverageTypeToUseForIcon(props.insuranceProduct);
  return getProductTypeIcon(useCoverageCode);
};

const showAdditionalWarning = () => {
  const status = policyStatusDetails.value;
  const product = props.insuranceProduct;
  const fields = props.fields;

  // default/reset
  isOverDueOrCancelled.value = false;
  warningMessage.value = '';

  // 1) Cancelled / future-cancelled
  if (product.status == PolicyStatus.CANCELLED) {
    isOverDueOrCancelled.value = true;
    warningMessage.value = replaceBookmark(
      Bookmarks.cancellationDate,
      isAutoCancelledPolicyInGracePeriod(product)
        ? (fields?.cancelledTextAuto?.value ?? '')
        : (fields?.cancelledText?.value ?? ''),
      fmtDate(product?.cancellationDate)
    );
    return;
  }

  // 2) Overdue (responsive vs non-responsive)
  if (status?.showOverdue) {
    warningMessage.value = status.isValidDelinquency
      ? (fields?.insuranceDelinquentPaymentText?.value ?? '')
      : status.isResponsivePayment
        ? (fields?.insuranceResponsiveOverdueText?.value ?? '')
        : (fields?.insuranceNonResponsiveOverdueText?.value ?? '');
    isOverDueOrCancelled.value = true;
    return;
  }

  // 3) Payment due (responsive) OR renewal pref off/not set
  // Note: Insurance Product Card uses renewalTermStart this is in effect the same date as the termEnd date which is what is used on the Product Details page.
  /// just mentioning to prevent any confusion in future as similar logic applies between the the components.
  if (
    (product?.state?.isPaymentDue && status?.isResponsivePayment) ||
    status?.isInRenewalWithRenewalPreferenceSetToOffOrNot
  ) {
    if (product?.state?.isInRenewal) {
      let renewalContent = '';
      if (product?.renewalTermAcceptance === undefined) {
        renewalContent = fields?.renewalDueResponsivePolicy?.value ?? '';
      } else if (product?.renewalTermAcceptance === true) {
        renewalContent = fields?.renewalDueResponsivePolicyAccepted?.value ?? '';
      } else if (product?.renewalTermAcceptance === false) {
        renewalContent = fields?.renewalDueResponsivePolicyRejected?.value ?? '';
      }

      if (renewalContent !== '') {
        warningMessage.value = replaceBookmark(
          Bookmarks.renewalStartDate,
          renewalContent,
          fmtDate(product?.renewalTermStart)
        );
        return;
      }
    }

    if (warningMessage.value?.trim() === '') {
      warningMessage.value = fields?.paymentDueText?.value ?? '';
      return;
    }
  }

  // 4) In renewal with non-responsive payment
  if (status?.isInRenewalWithNonResponsivePayment) {
    warningMessage.value = replaceBookmark(
      Bookmarks.renewalStartDate,
      fields?.renewalDueNonResponsivePolicy?.value ?? '',
      fmtDate(product?.renewalTermStart)
    );
    return;
  }

  // 5) Renewal fully paid
  if (status?.renewalFullyPaid) {
    warningMessage.value = replaceBookmark(
      Bookmarks.renewalStartDate,
      fields?.renewalTermPaidText?.value ?? '',
      fmtDate(product?.renewalTermStart)
    );
  }

  // No conditions matched -> keep defaults (no warning)
};
const getProductTypeIcon = (coverageCode?: string): ImageFieldValue | undefined => {
  if (coverageCode) {
    switch (coverageCode.toLowerCase()) {
      case QuoteTypeCodes[InsuranceQuoteType.Contents].toLowerCase():
        return props.fields?.contentsIcon.value;
      case QuoteTypeCodes[InsuranceQuoteType.Home].toLowerCase():
        return props.fields?.homeIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.HomeAndContents].toLowerCase():
        return props.fields?.homeContentsIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.MotorComprehensive].toLowerCase():
        return props.fields?.motorIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.MotorFireTheftAndThirdPartyLiability].toLowerCase():
        return props.fields?.motorThirdPartyFireIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.MotorThirdPartyLiability].toLowerCase():
        return props.fields?.motorThirdPartyIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.PetComp].toLowerCase():
        return props.fields?.petIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.BoatComprehensive].toLowerCase():
      case QuoteTypeCodes[InsuranceQuoteType.BoatThirdParty].toLowerCase():
        return props.fields?.boatIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.Caravan].toLowerCase():
        return props.fields?.caravanIcon?.value;
      case QuoteTypeCodes[InsuranceQuoteType.Motorcycle].toLowerCase():
        return props.fields?.motorcycleIcon?.value;
    }
  }
  return props.fields?.motorIcon?.value;
};

const redirectToCyclone = () => {
  let querystring = replaceBookmark(
    Bookmarks.policyId,
    props.fields?.cycloneFormUrl?.value.querystring ?? '',
    props.insuranceProduct.policyNumber ?? ''
  );
  querystring = replaceBookmark(Bookmarks.coverageId, querystring, selectCoverage.value);
  window.open(props.fields?.cycloneFormUrl?.value.href + '?' + querystring);
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1))
      : (_openBlock(), _createBlock(ProductCard, {
          key: 1,
          title: title.value ?? '',
          icon: {
      value: {
        src: getIcon()?.src,
        height: '40',
        width: '40',
        alt: 'Product icon',
      },
    },
          subTitle: subTitle.value,
          renewalText: renewalText.value,
          productLinkUrl: linkUrl.value,
          dataTestId: _ctx.dataTestId
        }, _createSlots({
          bottomOuterContent: _withCtx(() => [
            _createVNode(ProductStatus, {
              message: warningMessage.value,
              productStatus: 
          isOverDueOrCancelled.value
            ? _unref(RacqProductStatus).Cancelled
            : policyStatusDetails.value?.renewalFullyPaid
              ? _unref(RacqProductStatus).Paid
              : _unref(RacqProductStatus).Scheduled
        ,
              makeAPaymentLink: _ctx.fields?.makeAPaymentLink,
              payButtonText: _ctx.fields?.payButtonText.value,
              renewButtonText: _ctx.fields?.renewButtonText.value,
              renewalOfferFormLink: _ctx.fields?.renewalOfferFormLink,
              policyNumber: _ctx.insuranceProduct.policyNumber,
              prn: _ctx.insuranceProduct.currentTermPaymentDetails.paymentReferenceNumber,
              showButton: policyStatusDetails.value?.showPaymentDue,
              showPayNowButton: policyStatusDetails.value?.showPayNowButton,
              dataTestId: _ctx.dataTestId
            }, null, 8, ["message", "productStatus", "makeAPaymentLink", "payButtonText", "renewButtonText", "renewalOfferFormLink", "policyNumber", "prn", "showButton", "showPayNowButton", "dataTestId"])
          ]),
          _: 2
        }, [
          (policyStatusDetails.value?.showCycloneBanner)
            ? {
                name: "topContent",
                fn: _withCtx(() => [
                  _createElementVNode("div", _hoisted_2, [
                    (isMultiCoveragePolicy.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                          _createVNode(_unref(RichText), {
                            tag: "span",
                            field: _ctx.fields?.updateCycloneDetailsMultiPolicyText
                          }, null, 8, ["field"]),
                          _createElementVNode("a", {
                            href: "#",
                            role: "button",
                            onClick: _cache[0] || (_cache[0] = (e) => (e.preventDefault(), (showCoverageModal.value = true)))
                          }, "Update now")
                        ]))
                      : (_openBlock(), _createBlock(_unref(RichText), {
                          key: 1,
                          "data-test-id": "cycloneText",
                          class: "text-sm",
                          field: { value: updatedCycloneText.value }
                        }, null, 8, ["field"]))
                  ])
                ]),
                key: "0"
              }
            : undefined
        ]), 1032, ["title", "icon", "subTitle", "renewalText", "productLinkUrl", "dataTestId"])),
    (isMultiCoveragePolicy.value)
      ? (_openBlock(), _createBlock(ModalDialog, {
          key: 2,
          modelValue: showCoverageModal.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showCoverageModal).value = $event)),
          size: "md",
          closeOnEsc: true,
          showXCloseButton: true
        }, {
          default: _withCtx(() => [
            _createVNode(SelectCoverageOption, {
              modelValue: selectCoverage.value,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((selectCoverage).value = $event)),
              coverages: _ctx.insuranceProduct.coverages,
              fields: _ctx.fields,
              onButtonClicked: redirectToCyclone
            }, null, 8, ["modelValue", "coverages", "fields"])
          ]),
          _: 1
        }, 8, ["modelValue"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})